import React, { useState, useEffect } from 'react';
import {
    Datagrid,
    DateField,
    TextField,
    ChipField,
    ShowButton,
    ReferenceManyField,
    useDataProvider,
    Loading,
    ListContextProvider,
} from 'react-admin';
import DeleteButton from '../../components/DeleteButton';
import MoneyField from '../../components/MoneyField';
import CountryField from '../../components/CountryField';
import StorageTypeField from '../../components/StorageTypeField';
import { Storage } from '../../types';
import { FunctionField } from 'react-admin';
import axios from 'axios';

const WarehouseField = ({ record }: { label: string; record?: Storage }) => {
    const storageRecord =
        record?.storagerecords?.length && record?.storagerecords[0];
    return <span>{storageRecord?.warehouse?.name}</span>;
};

const LocationField = ({ record }: { label: string; record?: Storage }) => {
    const storageRecord =
        record?.storagerecords?.length && record?.storagerecords[0];
    return <span>{storageRecord?.location?.path}</span>;
};

// Custom data provider that merges order storages with storage records
const createEnhancedDataProvider = (
    originalDataProvider: any,
    additionalStorages: any[]
) => ({
    ...originalDataProvider,
    getList: async (resource: string, params: any) => {
        if (resource === 'storages') {
            const originalResponse = await originalDataProvider.getList(
                resource,
                params
            );

            // Add serial numbers to original storages
            const originalWithSerial = originalResponse.data.map(
                (storage: any, index: number) => ({
                    ...storage,
                    serial: index + 1,
                    isFromRequest: false,
                })
            );
            console.log(
                '🏷️ Original storages with serial:',
                originalWithSerial
            );

            // Add serial numbers to additional storages
            const additionalWithSerial = additionalStorages.map(
                (storage: any, index: number) => ({
                    ...storage,
                    serial: originalWithSerial.length + index + 1,
                    isFromRequest: true,
                })
            );
            console.log(
                '🆕 Additional storages with serial:',
                additionalWithSerial
            );

            const mergedData = [...originalWithSerial, ...additionalWithSerial];
            const result = {
                data: mergedData,
                total: mergedData.length,
            };

            return result;
        }
        return originalDataProvider.getList(resource, params);
    },
    getManyReference: async (resource: string, params: any) => {
        if (resource === 'storages') {
            const originalResponse = await originalDataProvider.getManyReference(
                resource,
                params
            );

            // Add serial numbers to original storages
            const originalWithSerial = originalResponse.data.map(
                (storage: any, index: number) => ({
                    ...storage,
                    serial: index + 1,
                    isFromRequest: false,
                })
            );
            console.log(
                '🏷️ Original storages with serial:',
                originalWithSerial
            );

            // Add serial numbers to additional storages
            const additionalWithSerial = additionalStorages.map(
                (storage: any, index: number) => ({
                    ...storage,
                    serial: originalWithSerial.length + index + 1,
                    isFromRequest: true,
                })
            );
            console.log(
                '🆕 Additional storages with serial:',
                additionalWithSerial
            );

            const mergedData = [...originalWithSerial, ...additionalWithSerial];
            const result = {
                data: mergedData,
                total: mergedData.length,
            };

            return result;
        }
        return originalDataProvider.getManyReference(resource, params);
    },
});

// Enhanced component that includes both order storages and storage records with status "in"
const EnhancedOrderStoragesList = ({ record, ...props }) => {
    const [additionalStorages, setAdditionalStorages] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);
    const [mergedStorages, setMergedStorages] = useState<any[]>([]);
    const [mergedLoading, setMergedLoading] = useState(false);
    const originalDataProvider = useDataProvider();

    useEffect(() => {
        const fetchRequestStoragesAndStorages = async () => {
            if (!record?.id) {
                return;
            }

            setLoading(true);
            try {
                // Step 1: Get existing order storages to avoid duplication
                const orderStoragesResponse = await originalDataProvider.getList(
                    'storages',
                    {
                        pagination: { page: 1, perPage: 200 },
                        sort: { field: 'id', order: 'ASC' },
                        filter: { orderId: record.id },
                    }
                );

                // Step 2: Get all requests for this order
                const requestsResponse = await originalDataProvider.getList(
                    'requests',
                    {
                        pagination: { page: 1, perPage: 200 },
                        sort: { field: 'id', order: 'ASC' },
                        filter: { orderId: record.id },
                    }
                );

                console.log(
                    '📋 Found',
                    requestsResponse.data.length,
                    'requests for order',
                    record.id
                );

                let allRequestLines: any[] = [];

                // Step 3: Get request-lines for each request
                for (const request of requestsResponse.data) {
                    try {
                        const requestLinesResponse = await originalDataProvider.getList(
                            'request-lines',
                            {
                                pagination: { page: 1, perPage: 1000 },
                                sort: { field: 'id', order: 'ASC' },
                                filter: { requestId: request.id },
                            }
                        );

                        allRequestLines = [
                            ...allRequestLines,
                            ...requestLinesResponse.data,
                        ];
                        console.log(
                            '📦 Found',
                            requestLinesResponse.data.length,
                            'request-lines for request',
                            request.id
                        );
                    } catch (error) {
                        console.error(
                            '❌ Error fetching request-lines for request',
                            request.id,
                            ':',
                            error
                        );
                    }
                }

                if (allRequestLines.length > 0) {
                    console.log(
                        '✅ Found',
                        allRequestLines.length,
                        'total request-lines from all requests'
                    );

                    // Step 4: Extract unique storageIds from request-lines
                    const storageIds = [
                        ...new Set(
                            allRequestLines
                                .filter((line: any) => line.storage?.id) // Only lines with storage
                                .map((line: any) => line.storage.id)
                        ),
                    ];

                    // Step 4: Get existing storage IDs to avoid duplication
                    const existingStorageIds = new Set(
                        orderStoragesResponse.data.map(
                            (storage: any) => storage.id
                        )
                    );

                    // Step 5: Filter out duplicates
                    const newStorageIds = storageIds.filter(
                        id => !existingStorageIds.has(id)
                    );

                    // Step 6: Fetch complete storage information using storages list API
                    if (newStorageIds.length > 0) {
                        // Get auth token
                        const token = localStorage.getItem('feathers-jwt');
                        const apiBaseUrl =
                            process.env.NODE_ENV === 'production'
                                ? 'https://admin-api.boxitstorage.com'
                                : 'https://admin-api.staging.boxitstorage.com';

                        // Prepare request body for storages list API
                        const requestBody = {
                            limit: 200,
                            page: 1,
                            sortBy: 'id',
                            sortOrder: 'ASC',
                            ids: newStorageIds, // Filter by specific storage IDs
                        };

                        let storagesData = [];

                        try {
                            const response = await fetch(
                                `${apiBaseUrl}/api/v1/storages/list`,
                                {
                                    method: 'POST',
                                    headers: {
                                        'Content-Type': 'application/json',
                                        Authorization: `Bearer ${token}`,
                                    },
                                    body: JSON.stringify(requestBody),
                                }
                            );

                            if (!response.ok) {
                                throw new Error(
                                    `HTTP error! status: ${response.status}`
                                );
                            }

                            const storagesApiResponse = await response.json();
                            console.log(
                                '✅ Storages list API response:',
                                storagesApiResponse.results?.length,
                                'storages'
                            );

                            storagesData = storagesApiResponse.results || [];
                        } catch (error) {
                            console.error(
                                '❌ Error calling storages list API:',
                                error
                            );
                            // Fallback to empty array
                            storagesData = [];
                        }

                        // Mark as additional storages from requests
                        const storagesWithFlag = storagesData.map(
                            (storage: any) => {
                                // Find corresponding request-line for additional data
                                const correspondingLine = allRequestLines.find(
                                    (line: any) =>
                                        line.storage?.id === storage.id
                                );

                                return {
                                    ...storage,
                                    isFromRequest: true,
                                    requestLineData: correspondingLine,
                                    // Override some fields with request-line data if needed
                                    requestId: correspondingLine?.requestId,
                                    requestLineId: correspondingLine?.id,
                                };
                            }
                        );

                        setAdditionalStorages(storagesWithFlag);
                    } else {
                        setAdditionalStorages([]);
                    }
                } else {
                    setAdditionalStorages([]);
                }
            } catch (error) {
                console.error('❌ Error fetching request storages:', error);
                setAdditionalStorages([]);
            } finally {
                setLoading(false);
            }
        };

        fetchRequestStoragesAndStorages();
    }, [record?.id, record?.storagerecords, originalDataProvider]);

    useEffect(() => {
        const fetchMergedData = async () => {
            if (!record?.id) {
                return;
            }

            setMergedLoading(true);
            try {
                // Get original order storages
                const orderStoragesResponse = await originalDataProvider.getManyReference(
                    'storages',
                    {
                        target: 'orderId',
                        id: record.id,
                        pagination: { page: 1, perPage: 200 },
                        sort: { field: 'id', order: 'ASC' },
                        filter: {},
                    }
                );

                // Add serial numbers to original storages
                const originalWithSerial = orderStoragesResponse.data.map(
                    (storage: any, index: number) => ({
                        ...storage,
                        serial: index + 1,
                        isFromRequest: false,
                    })
                );

                // Add serial numbers to additional storages
                const additionalWithSerial = additionalStorages.map(
                    (storage: any, index: number) => ({
                        ...storage,
                        serial: originalWithSerial.length + index + 1,
                        isFromRequest: true,
                    })
                );

                const merged = [...originalWithSerial, ...additionalWithSerial];
                setMergedStorages(merged);
            } catch (error) {
                console.error('❌ Error fetching merged data:', error);
                setMergedStorages([]);
            } finally {
                setMergedLoading(false);
            }
        };

        if (!loading) {
            fetchMergedData();
        }
    }, [record?.id, originalDataProvider, additionalStorages, loading]);

    // Create enhanced data provider
    const enhancedDataProvider = createEnhancedDataProvider(
        originalDataProvider,
        additionalStorages
    );

    if (loading || mergedLoading) {
        return <Loading />;
    }

    // Don't render if no data is available yet
    if (!mergedStorages || mergedStorages.length === 0) {
        return <div>No storages found for this order.</div>;
    }

    // Convert array to object with ids as keys (react-admin format)
    const dataById = mergedStorages.reduce((acc: any, storage: any) => {
        acc[storage.id] = storage;
        return acc;
    }, {});

    const ids = mergedStorages.map(storage => storage.id);

    // Create a proper list context with all required properties
    const listContext = {
        data: dataById,
        ids: ids,
        total: mergedStorages.length,
        loaded: true,
        loading: false,
        error: null,
        resource: 'storages',
        basePath: '/storages',
        currentSort: { field: 'id', order: 'ASC' },
        displayedFilters: {},
        filterValues: {},
        hasCreate: false,
        hideFilter: () => {},
        page: 1,
        perPage: 200,
        selectedIds: [],
        setFilters: () => {},
        setPage: () => {},
        setPerPage: () => {},
        setSort: () => {},
        showFilter: () => {},
        onSelect: () => {},
        onToggleItem: () => {},
        onUnselectItems: () => {},
    };

    return (
        <ListContextProvider value={listContext}>
            <Datagrid>
                <TextField source="serial" sortable={false} />
                <TextField source="id" />
                <TextField source="code" />
                <StorageTypeField source="storagetypeId" label="Type" />
                <TextField source="qty" />
                <TextField source="size" label="CBM" />
                <FunctionField
                    label="Total size"
                    render={record =>
                        record.qty !== undefined && record.size !== undefined
                            ? record.qty * record.size
                            : ''
                    }
                />
                <CountryField />
                <ChipField source="status" />
                <DateField locales="en-IN" source="from" />
                <DateField locales="en-IN" source="to" />
                <WarehouseField label="Warehouse" />
                <LocationField label="Location" />
                {/* <FunctionField
                    label="Source"
                    render={record =>
                        record.isFromStorageRecord
                            ? 'Storage Record'
                            : 'Order Storage'
                    }
                /> */}
                <ShowButton />
            </Datagrid>
        </ListContextProvider>
    );
};

// Keep the original component for backward compatibility
export const OrderStoragesList = props => (
    <EnhancedOrderStoragesList {...props} />
);
